import { type CellPromptRecord } from "@istari-frontend/notebook/model";

export type CellPromptProvenance = {
  record: CellPromptRecord;
  // The shared context block, stored once per fingerprint at notebook level.
  context: string;
};

// A notebook read back from storage is attacker-controllable (any .ipynb in S3), and these records
// are relayed into resource comments — so bound every field on the way in.
const MAX_RECORDS_PER_CELL = 50;
const MAX_PROMPT_CHARS = 4000;
const MAX_RESOLVED_CONTEXT_CHARS = 8000;
const MAX_CONTEXT_BLOB_CHARS = 200_000;
const MAX_CONTEXT_BLOBS = 50;

/**
 * Stable, non-cryptographic fingerprint of a context block (FNV-1a, hex).
 *
 * Its only job is to tell whether two generations saw the same context and to key the deduplicated
 * blob store — not to resist tampering, hence no crypto and no async.
 */
export function contextKeyFor(context: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < context.length; i++) {
    hash ^= context.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${hash.toString(16).padStart(8, "0")}${context.length.toString(16)}`;
}

/**
 * Splits a generation's context in two so provenance stays cheap: the shared block (instruction +
 * notebook context) is fingerprinted and stored once, while the per-request slice the host resolved
 * for this prompt rides on the record itself.
 */
export function createProvenance(input: {
  mode: CellPromptRecord["mode"];
  prompt: string;
  context: string;
  resolvedContext: string | null;
  createdAt: string;
}): CellPromptProvenance {
  return {
    record: {
      id: crypto.randomUUID(),
      mode: input.mode,
      prompt: input.prompt.slice(0, MAX_PROMPT_CHARS),
      createdAt: input.createdAt,
      contextKey: contextKeyFor(input.context),
      ...(input.resolvedContext ? { resolvedContext: input.resolvedContext.slice(0, MAX_RESOLVED_CONTEXT_CHARS) } : {}),
    },
    context: input.context,
  };
}

function isMode(value: unknown): value is CellPromptRecord["mode"] {
  return value === "code" || value === "text";
}

export function toValidPromptRecords(value: unknown): CellPromptRecord[] {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, MAX_RECORDS_PER_CELL)
    .map((raw): CellPromptRecord | null => {
      if (!raw || typeof raw !== "object") return null;
      const record = raw as Record<string, unknown>;
      if (typeof record.prompt !== "string" || !isMode(record.mode)) return null;
      return {
        id: typeof record.id === "string" ? record.id : crypto.randomUUID(),
        mode: record.mode,
        prompt: record.prompt.slice(0, MAX_PROMPT_CHARS),
        createdAt: typeof record.createdAt === "string" ? record.createdAt : "",
        contextKey: typeof record.contextKey === "string" ? record.contextKey : "",
        ...(typeof record.resolvedContext === "string"
          ? { resolvedContext: record.resolvedContext.slice(0, MAX_RESOLVED_CONTEXT_CHARS) }
          : {}),
      };
    })
    .filter((record): record is CellPromptRecord => record !== null);
}

export function toValidPromptContexts(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const entries = Object.entries(value as Record<string, unknown>)
    .filter((entry): entry is [string, string] => typeof entry[1] === "string")
    .slice(0, MAX_CONTEXT_BLOBS)
    .map(([key, blob]): [string, string] => [key, blob.slice(0, MAX_CONTEXT_BLOB_CHARS)]);
  return Object.fromEntries(entries);
}

/**
 * The audit note posted as a comment when a cell is saved as a resource: what was asked, when, and
 * the context that shaped it. Hand-written cells get an explicit statement rather than silence.
 */
export function formatPromptAudit(input: {
  notebookName: string;
  records: CellPromptRecord[];
  contexts: Record<string, string>;
}): string {
  const { notebookName, records, contexts } = input;
  if (records.length === 0) {
    return `Saved from notebook "${notebookName}". This cell was written by hand — no AI generation was recorded for it.`;
  }

  const lines = [
    `Saved from notebook "${notebookName}".`,
    records.length === 1 ? "Generated from 1 recorded prompt:" : `Generated from ${records.length} recorded prompts:`,
  ];

  records.forEach((record, index) => {
    const when = record.createdAt ? ` — ${record.createdAt}` : "";
    lines.push("", `${index + 1}. [${record.mode} cell${when}] ${record.prompt}`);
    lines.push(`   Context fingerprint: ${record.contextKey || "not recorded"}`);
    if (record.resolvedContext) {
      lines.push("   Context resolved for this request:", indent(record.resolvedContext));
    }
  });

  // Appended once per distinct fingerprint rather than per prompt: successive generations in a
  // session almost always share one context, and the block runs to several thousand characters.
  const referenced = [...new Set(records.map((record) => record.contextKey))].filter((key) => contexts[key]);
  if (referenced.length > 0) {
    lines.push("", "Context supplied to the model, for reproducibility:");
    for (const key of referenced) {
      lines.push("", `[fingerprint ${key}]`, indent(contexts[key]));
    }
  }

  lines.push(
    "",
    "Note: prompts are recorded per generation. Manual edits made to the cell afterwards are not captured here.",
  );
  return lines.join("\n");
}

function indent(text: string): string {
  return text
    .split("\n")
    .map((line) => `     ${line}`)
    .join("\n");
}
